import { addDays, addMonths, format, isValid, parse, startOfMonth, startOfWeek } from 'date-fns';

/**
 * Time windows for `GET /social/schedule/posts`. Day boundaries come from
 * date-fns day math, never from millisecond arithmetic: a month with a DST
 * change has a 23-hour and a 25-hour day, and a grid built from
 * `n * 86_400_000` drifts an hour on every cell after it.
 *
 * The zone is the one the dates passed in carry: date-fns keeps a `TZDate`'s
 * zone through every function here, so a `now` from `scheduleSettings.inZone`
 * gives a grid of that zone's midnights, and a plain `Date` the browser's.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The widest window the list endpoint accepts.
 * FIXME(api-mirror): mirrors `SocialPostsLogic.MAX_LIST_WINDOW_MS` in the
 * atlas repo; there is no shared package to import it from. Retire this copy
 * when the API exposes its limits. Acceptable once.
 */
export const MAX_LIST_WINDOW_MS = 45 * DAY_MS;

/** As the API takes them: `publishAt` strictly between the two. */
export type MsRange = { newerThan: number; olderThan: number };

/** The half-open local-day span `[start, end)` as an exclusive API range. */
export const rangeOfDays = (start: Date, end: Date): MsRange => ({
  newerThan: start.getTime() - 1,
  olderThan: end.getTime(),
});

export const rangeKey = (range: MsRange): string => `${range.newerThan}:${range.olderThan}`;

export const isInRange = (publishAt: number, range: MsRange): boolean =>
  publishAt > range.newerThan && publishAt < range.olderThan;

// ── Month parameter ────────────────────────────────────────────

const MONTH_PARAM_FORMAT = 'yyyy-MM';

/** First of the month, in `now`'s zone. Anything unparseable is the current month. */
export const parseMonthParam = (value: string | null, now: Date): Date => {
  if (value) {
    const parsed = parse(value, MONTH_PARAM_FORMAT, now);
    if (isValid(parsed)) return startOfMonth(parsed);
  }
  return startOfMonth(now);
};

export const formatMonthParam = (month: Date): string => format(month, MONTH_PARAM_FORMAT);

/** The month's own days, `[1st, 1st of next)`. A subset of its grid, so it needs no request of its own. */
export const monthRange = (month: Date): MsRange => {
  const first = startOfMonth(month);
  return rangeOfDays(first, addMonths(first, 1));
};

// ── Calendar grid ──────────────────────────────────────────────

export const CALENDAR_WEEK_STARTS_ON = 1; // Monday
export const CALENDAR_ROWS = 6;
export const CALENDAR_DAYS = CALENDAR_ROWS * 7;

export type CalendarGrid = {
  /** First of the visible month. */
  month: Date;
  /** Midnight of every cell in the month's zone, `CALENDAR_DAYS` of them, first row first. */
  days: Date[];
  /** One list request covers the whole grid, and so the month inside it: 42 days is under the 45-day cap even across a DST change. */
  range: MsRange;
};

/**
 * Always six rows so the layout never jumps between months, and the window
 * is a constant 42 days.
 */
export const calendarGrid = (month: Date): CalendarGrid => {
  const first = startOfMonth(month);
  const gridStart = startOfWeek(first, { weekStartsOn: CALENDAR_WEEK_STARTS_ON });
  const days = Array.from({ length: CALENDAR_DAYS }, (_, i) => addDays(gridStart, i));
  return { month: first, days, range: rangeOfDays(gridStart, addDays(gridStart, CALENDAR_DAYS)) };
};

// ── Minute refresh ─────────────────────────────────────────────

const REFRESH_LOOKBACK_MS = 30 * 60 * 1000;
/**
 * How far ahead a refresh looks. Also what "due" means to the store when it
 * decides whether a tick is worth a request, so the two can never disagree.
 */
export const REFRESH_LOOKAHEAD_MS = 60 * 1000;

/**
 * What one refresh asks for: everything due within a minute and everything
 * that went due in the last half hour, widened back to the oldest post we
 * still hold as unsettled (a large video sits in `processing` for many
 * minutes), and clamped to the widest window the API accepts.
 */
export const refreshRange = (now: number, oldestUnsettledDue: number | null): MsRange => {
  const olderThan = now + REFRESH_LOOKAHEAD_MS;
  const wanted = Math.min(now - REFRESH_LOOKBACK_MS, oldestUnsettledDue ?? Infinity) - 1;
  return { newerThan: Math.max(wanted, olderThan - MAX_LIST_WINDOW_MS), olderThan };
};
